import { useCallback, useEffect, useState } from 'react';
import { Button, Link } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import UserBundleService from '../../services/BundleService';
import CallButton from '../Common/CallButton/CallButton';
import { UserType } from '../../enums/common';
import moment from 'moment';

interface InputProps {
    houseId: number;
}

const userBundleService = new UserBundleService();

const HousesUserBundleTab = ({ houseId }: InputProps) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const refreshData = useCallback(() => {
        setLoading(true);
        userBundleService
            .getUserBundleMappingForHouse(houseId, rowsPerPage, page * rowsPerPage, { field: 'id', order: 'DESC' })
            .then((res) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [houseId, page, rowsPerPage]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const columns: CommonListColumn[] = [
        { label: 'Id', field: 'id' },
        {
            label: 'House',
            render: (row) => (
                <Link href={`/#/Houses/${row?.houseId}/show`} underline="hover">{row?.houseId}</Link>
            ),
        },
        {
            label: 'Bundle',
            render: (row) => (
                <Link href={`/#/Bundles/${row?.bundleId}/show`} underline="hover">{row?.bundleId}</Link>
            ),
        },
        {
            label: 'User Mobile',
            stopRowClick: true,
            render: (row) => Array.isArray(row?.house?.customers)
                ? row.house.customers.map((c: any) => (
                    <CallButton key={c.mobile} toPhoneNumber={c?.mobile} userType={UserType.CUSTOMER} />
                ))
                : '-',
        },
        {
            label: 'Start Date',
            render: (row) => row?.startDate ? moment(row.startDate, 'YYYY-MM-DD').format('DD MMM YYYY') : '-',
        },
        { label: 'Start Slot', field: 'startSlot' },
        {
            label: 'End Date',
            render: (row) => row?.endDate ? moment(row.endDate, 'YYYY-MM-DD').format('DD MMM YYYY') : '-',
        },
        { label: 'End Slot', field: 'endSlot' },
        { label: 'Status', field: 'status', type: 'status' },
        { label: 'Quoted Net Price', field: 'quotedNetPrice' },
        {
            label: 'Meal Mapping',
            render: (row) => row?.userBundleMeta?.mealMapping
                ? JSON.stringify(row.userBundleMeta.mealMapping)
                : '-',
        },
        { label: 'Cook Cut', field: 'cookCut' },
        {
            label: 'Actions',
            stopRowClick: true,
            render: (row) => (
                <Link href={`/#/UserBundles/${row.id}/show`}>
                    <Button variant="contained" color="primary" size="small">Show</Button>
                </Link>
            ),
        },
    ];

    return (
        <CommonListTable
            columns={columns}
            data={data}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            emptyText="No User Bundle Mapping Found"
            headerRight={
                <Link href="/#/UserBundles/create" underline="none">
                    <Button variant="contained" color="secondary" size="small">
                        Create User Bundle
                    </Button>
                </Link>
            }
        />
    );
};

export default HousesUserBundleTab;
